import { Link, useNavigate } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { useRef } from 'react';
import type { SportFilter } from '../../api/keys';
import { plural } from '../../lib/plural';
import { sportSearch } from '../activities/activitiesModel';
import { BottomSheet } from '../../ui/BottomSheet';
import { useModalFocus } from '../../ui/useModalFocus';
import { DropZone } from './DropZone';
import type { UploadItem } from './uploadQueue';
import { useUploadQueue } from './UploadQueueProvider';
import { UploadRow } from './UploadRow';
import styles from './upload.module.css';

function QueueList({ items, onRetry, onClear }: { items: UploadItem[]; onRetry: (id: string) => void; onClear: () => void }) {
  return (
    <div className={styles.queue}>
      <div className={styles.queueHead}>
        <span>THIS UPLOAD · {items.length} {plural(items.length, 'FILE')}</span>
        <button type="button" className={styles.clear} onClick={onClear}>CLEAR FINISHED</button>
      </div>
      <ul className={styles.rows} aria-label="Upload queue">
        {items.map((item) => <UploadRow key={item.id} item={item} onRetry={onRetry} />)}
      </ul>
    </div>
  );
}

export function UploadPanel({ sport }: { sport: SportFilter }) {
  const queue = useUploadQueue();
  const navigate = useNavigate();
  const closeSearch = sportSearch(sport);
  const dialogRef = useRef<HTMLElement>(null);
  useModalFocus(dialogRef, () => void navigate({ to: '/activities', search: closeSearch }));

  return (
    <>
      <Link to="/activities" search={closeSearch} className={styles.scrim} data-motion="scrim" aria-label="Close upload panel" tabIndex={-1} />
      <BottomSheet fill>
        <aside ref={dialogRef} tabIndex={-1} className={styles.panel} data-motion="panel" role="dialog" aria-modal="true" aria-labelledby="upload-title">
          <div className={styles.body}>
            <header className={styles.head}>
              <div className={styles.headText}>
                <p className={styles.eyebrow}>ADD ACTIVITY</p>
                <h2 id="upload-title" className={styles.title}>UPLOAD .FIT FILES</h2>
              </div>
              <Link to="/activities" search={closeSearch} className="icon-button" aria-label="Close"><X size={16} aria-hidden="true" /></Link>
            </header>
            <DropZone onFiles={queue.addFiles} />
            {queue.items.length > 0 && <QueueList items={queue.items} onRetry={queue.retry} onClear={queue.clearFinished} />}
            <p className={styles.note}>Rides that are also on Strava are matched by start time. The .FIT version wins because it carries every data point.</p>
          </div>
          <Link to="/activities" search={closeSearch} className={styles.done}>DONE</Link>
        </aside>
      </BottomSheet>
    </>
  );
}
